import React, { useEffect, useState } from 'react';
import { Button, Input, InputNumber, Space, Table, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import UploadImage from '@/components/UploadImage';
import { LoraConfig, getLoraList, batchSaveLora } from '../api';

interface LoraConfigPanelProps {
  styleId?: number;
  styleSource?: string;
  model?: number | string;
  supportLora?: number;
}

/**
 * LoRA 配置面板 - 按引擎/架构分流
 *
 *   model=5  (SuppX ComfyUI)      → 文件路径列 (loraFilePath)
 *   model=20 (Runware Flux Klein) → AIR ID 列 (loraAirId)      架构=runware-flux
 *   model=21 (Replicate FluxPro)  → LoRA URL 列 (loraUrl)      架构=flux-pro
 *   model=22 (Runware Flux.1-dev) → AIR ID 列 (loraAirId)      架构=runware-flux
 *   model=23 (Runware Illustrious)→ AIR ID 列 (loraAirId)      架构=runware-sdxl
 *   model=24 (Runware NoobAI)     → AIR ID 列 (loraAirId)      架构=runware-sdxl
 *
 * 拓展性：后续加 Runware 新模型，只需扩 RUNWARE_AIR_ID_MODELS 列表
 */

// 使用 AIR ID 的模型（Runware 系列，统一格式 civitai:xxx@yyy 或 runware:xxx@yyy）
const RUNWARE_AIR_ID_MODELS = new Set(['20', '22', '23', '24']);
// 使用 HTTPS URL 的模型（Replicate 系列，OSS/HF 直链）
const REPLICATE_URL_MODELS = new Set(['21']);

const LoraConfigPanel: React.FC<LoraConfigPanelProps> = ({ styleId, styleSource = 'sx', model, supportLora }) => {
  const [loraList, setLoraList] = useState<LoraConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const modelStr = String(model ?? '');
  const isRunwareAirId = RUNWARE_AIR_ID_MODELS.has(modelStr);
  const isReplicateUrl = REPLICATE_URL_MODELS.has(modelStr);
  const isComfyUiFilePath = !isRunwareAirId && !isReplicateUrl;

  // 权重范围：Runware Flux Klein 允许 -4~4，其他 Runware 0~2（SDXL/Flux Dev），Replicate 0~2，ComfyUI 0~1
  const weightMin = modelStr === '20' ? -4 : 0;
  const weightMax = modelStr === '20' ? 4 : (isRunwareAirId || isReplicateUrl) ? 2 : 1;

  // 引擎提示文案
  const engineTag = (() => {
    if (modelStr === '20') return 'Runware Flux Klein (最多 2 个 LoRA 混合)';
    if (modelStr === '21') return 'Replicate Flux.1-dev (最多 2 个)';
    if (modelStr === '22') return 'Runware Flux.1-dev (最多 2 个)';
    if (modelStr === '23') return 'Runware Illustrious-XL';
    if (modelStr === '24') return 'Runware NoobAI-XL';
    return 'SuppX (ComfyUI)';
  })();

  useEffect(() => {
    if (styleId && supportLora === 1) {
      setLoading(true);
      getLoraList(styleId, styleSource)
        .then(setLoraList)
        .catch(() => setLoraList([]))
        .finally(() => setLoading(false));
    }
  }, [styleId, styleSource, supportLora]);

  if (supportLora !== 1) return null;
  if (!styleId) return <div style={{ color: '#999', fontSize: 12 }}>请先保存风格后配置 Lora</div>;

  const addLora = () => {
    setLoraList([...loraList, { loraName: '', defaultWeight: 0.8 }]);
  };

  const removeLora = (index: number) => {
    setLoraList(loraList.filter((_, i) => i !== index));
  };

  const updateLora = (index: number, field: keyof LoraConfig, value: any) => {
    const newList = [...loraList];
    (newList[index] as any)[field] = value;
    setLoraList(newList);
  };

  const handleSave = async () => {
    if (!styleId) return;
    setSaving(true);
    try {
      // 保存前统一 trim 前后空格（避免运营手滑带空格导致 Runware/Replicate API failedLoraLoad）
      const sanitized = loraList.map(l => ({
        ...l,
        loraName: l.loraName?.trim() || '',
        loraNameEn: l.loraNameEn?.trim(),
        loraNameJa: l.loraNameJa?.trim(),
        loraFilePath: l.loraFilePath?.trim(),
        loraAirId: l.loraAirId?.trim(),
        loraUrl: l.loraUrl?.trim(),
        bigModel: l.bigModel?.trim(),
      }));
      await batchSaveLora(styleId, styleSource, sanitized);
      message.success('Lora 配置保存成功');
      const fresh = await getLoraList(styleId, styleSource);
      setLoraList(fresh);
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Lora名称(开发用)',
      dataIndex: 'loraName',
      render: (_: any, __: any, index: number) => (
        <Input value={loraList[index].loraName} onChange={e => updateLora(index, 'loraName', e.target.value)} placeholder="名称标识" size="small" />
      ),
    },
    {
      title: '英文名(用户)',
      dataIndex: 'loraNameEn',
      render: (_: any, __: any, index: number) => (
        <Input value={loraList[index].loraNameEn} onChange={e => updateLora(index, 'loraNameEn', e.target.value)} placeholder="English name" size="small" />
      ),
    },
    {
      title: '日文名(用户)',
      dataIndex: 'loraNameJa',
      render: (_: any, __: any, index: number) => (
        <Input value={loraList[index].loraNameJa} onChange={e => updateLora(index, 'loraNameJa', e.target.value)} placeholder="Japanese name" size="small" />
      ),
    },
    {
      title: '默认权重',
      dataIndex: 'defaultWeight',
      width: 100,
      render: (_: any, __: any, index: number) => (
        <InputNumber
          value={loraList[index].defaultWeight}
          onChange={v => updateLora(index, 'defaultWeight', v)}
          min={weightMin}
          max={weightMax}
          step={0.1}
          size="small"
        />
      ),
    },
    // ComfyUI 文件路径列（model=5 SuppX）
    ...(isComfyUiFilePath ? [{
      title: '文件路径(ComfyUI)',
      dataIndex: 'loraFilePath',
      render: (_: any, __: any, index: number) => (
        <Input value={loraList[index].loraFilePath} onChange={(e: any) => updateLora(index, 'loraFilePath', e.target.value)} placeholder="ComfyUI文件路径" size="small" />
      ),
    }] : []),
    // Runware AIR ID 列（model=20/22/23/24，统一格式）
    ...(isRunwareAirId ? [{
      title: 'AIR ID(Runware)',
      dataIndex: 'loraAirId',
      render: (_: any, __: any, index: number) => (
        <Input
          value={loraList[index].loraAirId}
          onChange={(e: any) => updateLora(index, 'loraAirId', e.target.value)}
          placeholder="civitai:xxx@yyy 或 runware:xxx@yyy"
          size="small"
        />
      ),
    }] : []),
    // Replicate LoRA URL 列（model=21 SuppFluxPro，OSS/HF 直链）
    ...(isReplicateUrl ? [{
      title: 'LoRA URL(Replicate)',
      dataIndex: 'loraUrl',
      render: (_: any, __: any, index: number) => (
        <Input
          value={loraList[index].loraUrl}
          onChange={(e: any) => updateLora(index, 'loraUrl', e.target.value)}
          placeholder="https://.../xxx.safetensors (OSS/HF/CivitAI 直链)"
          size="small"
        />
      ),
    }] : []),
    {
      title: '预览图',
      dataIndex: 'loraImageUrl',
      width: 120,
      render: (_: any, __: any, index: number) => (
        <UploadImage
          value={loraList[index].loraImageUrl}
          onChange={(url: string) => updateLora(index, 'loraImageUrl', url)}
          width={80}
          height={80}
          showSizeHint={false}
        />
      ),
    },
    {
      title: '大模型',
      dataIndex: 'bigModel',
      render: (_: any, __: any, index: number) => (
        <Input value={loraList[index].bigModel} onChange={e => updateLora(index, 'bigModel', e.target.value)} placeholder="大模型名称" size="small" />
      ),
    },
    {
      title: '操作',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Popconfirm title="确定删除?" onConfirm={() => removeLora(index)}>
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 16, padding: '12px', border: '1px solid #d9d9d9', borderRadius: 8, background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Lora 配置（共 {loraList.length} 个）- {engineTag}</strong>
        <Space>
          <Button type="dashed" icon={<PlusOutlined />} onClick={addLora} size="small">添加 Lora</Button>
          <Button type="primary" onClick={handleSave} loading={saving} size="small">保存 Lora 配置</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={loraList}
        rowKey={(_, i) => String(i)}
        pagination={false}
        loading={loading}
        size="small"
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default LoraConfigPanel;
